import React from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Users, Target, PlayCircle } from "lucide-react";

const ICONS = [Users, Target, PlayCircle];

export default function PlayModes() {
    const reduce = useReducedMotion();
    const { t } = useTranslation();
    const modes = t("playModes.modes", { returnObjects: true });
    const safeModes = Array.isArray(modes) ? modes : [];
    return (
        <section id="play-modes" className="py-16 sm:py-20 lg:py-24 bg-[#F4EBDD] relative overflow-hidden" data-testid="play-modes-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="flex flex-col items-start gap-4 max-w-2xl">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#FBF7EE] border border-[#E9D9C1] px-3 py-1 text-xs font-semibold text-[#0B4A34] uppercase tracking-wider">{t("playModes.eyebrow")}</span>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#062A1E]">{t("playModes.title")}</h2>
                    <p className="text-[#1B2621]/75 text-base sm:text-lg">{t("playModes.subtitle")}</p>
                </div>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                    {safeModes.map((m, idx) => {
                        const Icon = ICONS[idx] || Users;
                        return (
                            <motion.a
                                key={idx}
                                href="#waitlist"
                                aria-label={`${m.title} — ${t("hero.notifyButton")}`}
                                initial={reduce ? false : { opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.5, delay: idx * 0.07 }}
                                className="group relative rounded-2xl border border-[#E9D9C1] bg-[#FBF7EE] p-6 sm:p-7 shadow-[0_10px_30px_rgba(6,42,30,0.06)] hover:shadow-[0_18px_50px_rgba(6,42,30,0.12)] hover:-translate-y-0.5 hover:border-[#137A55]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4A34] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4EBDD] transition-shadow transition-transform duration-200 overflow-hidden cursor-pointer"
                                data-testid={`play-mode-${idx}`}
                            >
                                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[radial-gradient(closest-side,rgba(19,122,85,0.12),transparent_70%)]" aria-hidden />
                                <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0B4A34] to-[#137A55] text-[#FBF7EE] shadow-[0_10px_24px_rgba(6,42,30,0.25)]"><Icon className="h-7 w-7" /></span>
                                <div className="relative mt-5">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F4EBDD] border border-[#E9D9C1] px-2.5 py-0.5 text-[10px] font-semibold text-[#8B6F2A] uppercase tracking-wider">{m.eyebrow}</span>
                                    <h3 className="mt-3 font-display text-2xl text-[#062A1E] leading-tight">{m.title}</h3>
                                    <p className="mt-2 text-sm text-[#1B2621]/80 leading-relaxed">{m.desc}</p>
                                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B4A34] group-hover:text-[#137A55] transition-colors duration-150" data-testid={`play-mode-${idx}-cta`}>
                                        {m.cta}
                                        <span aria-hidden className="transition-transform duration-150 group-hover:translate-x-0.5">→</span>
                                    </span>
                                </div>
                            </motion.a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
